import { gql } from '@apollo/client'

import { AUDIT_ATTRIBUTES } from '../audits/graphQL'
import {
  CONVERSATION_THREAD_ATTRIBUTES,
  CONVERSATION_THREAD_ENTITY_ATTRIBUTES,
  KNOCK_MESSAGE_LOG_ATTRIBUTES,
} from '../conversationThreads/graphQL'
import { PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES } from '../projects/graphQL'

const ACTIONS_ATTRIBUTES = gql`
  fragment actionsAttributes on PlatformAction {
    id
    createdAt
    updatedAt
    archivedAt
    remindedAt

    projectId
    project {
      id
      name
      status
      timezone
    }

    type
    title
    description
    status
    priorityType
    dueDate

    projectId
    locationId

    attachments {
      id
      key
      url
      fileName
    }

    templateResponse {
      id
      fullIdentifier
      template {
        ... on Template {
          id
          templateName
          templateType
          templateData
        }
        ... on LibraryTemplate {
          id
          templateName
          templateType
          templateData
        }
      }
    }

    templateResponseBlockData

    assignees {
      id
      worker {
        id
        user {
          id
          firstName
          lastName
        }
      }
      adminUser {
        id
        user {
          id
          firstName
          lastName
        }
      }
      subcontractor {
        id
        company {
          name
        }
      }
    }

    config {
      requireAttachmentToClose
    }

    conversation {
      ...conversationThreadAttributes
    }
  }
  ${CONVERSATION_THREAD_ATTRIBUTES}
`

export const GET_ACTIONS = gql`
  query platformActions_UI(
    $filter: PlatformActionFilterInput
    $pagination: PaginationInput
    $sort: PlatformActionSortInput
  ) {
    platformActions(filter: $filter, pagination: $pagination, sort: $sort) {
      id
      createdAt
      archivedAt
      remindedAt

      completedAt

      type
      title
      description
      status
      priorityType
      dueDate

      locationId
      projectId

      project {
        id
        name
        status
      }

      attachments {
        id
        key
        url
        fileName
      }

      assignees {
        id
        worker {
          id
          user {
            id
            firstName
            lastName
          }
        }
        adminUser {
          id
          user {
            id
            firstName
            lastName
          }
        }
        subcontractor {
          id
          company {
            name
          }
        }
      }

      templateResponse {
        id
        fullIdentifier
        template {
          ... on Template {
            id
            templateName
          }
          ... on LibraryTemplate {
            id
            templateName
          }
        }
      }
    }

    platformActionsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_ACTIONS_AGGREGATE_STATS = gql`
  query platformActionsAggregateStats_UI($filter: PlatformActionFilterInput) {
    platformActionsAggregateStats(filter: $filter) {
      status
      count
    }
  }
`

export const GET_ACTION = gql`
  query platformAction_UI($id: String!) {
    platformAction(id: $id) {
      ...actionsAttributes
      isOwner
    }
  }
  ${ACTIONS_ATTRIBUTES}
`

export const GET_ACTION_WITH_AUDIT_LOGS = gql`
  query platformActionWithAuditLogs_UI($id: String!) {
    platformAction(id: $id) {
      ...actionsAttributes
      isOwner
    }
    auditLogsByResourceId(id: $id) {
      ...auditAttributes
    }
    knockMessageLogs_admin(filter: { platformActionId: $id }) {
      ...knockMessageLogAttributes
    }
  }
  ${ACTIONS_ATTRIBUTES}
  ${AUDIT_ATTRIBUTES}
  ${KNOCK_MESSAGE_LOG_ATTRIBUTES}
`

export const CREATE_ACTION = gql`
  mutation createPlatformAction_UI($input: CreatePlatformActionInput!) {
    createPlatformAction(input: $input) {
      ...actionsAttributes
    }
  }
  ${ACTIONS_ATTRIBUTES}
`

export const UPDATE_ACTION = gql`
  mutation updatePlatformAction_UI(
    $id: String!
    $input: UpdatePlatformActionInput!
  ) {
    updatePlatformAction(id: $id, input: $input) {
      ...actionsAttributes
    }
  }
  ${ACTIONS_ATTRIBUTES}
`

export const ARCHIVE_ACTION = gql`
  mutation archivePlatformAction_UI($id: String!) {
    archivePlatformAction(id: $id) {
      id
    }
  }
`

export const RESTORE_ACTION = gql`
  mutation restorePlatformAction_UI($id: String!) {
    restorePlatformAction(id: $id) {
      id
    }
  }
`

export const RESEND_ACTION_EMAILS = gql`
  mutation resendPlatformActionEmail_UI($id: String!) {
    resendPlatformActionEmail(id: $id) {
      id
    }
  }
`

export const RESEND_MULTIPLE_ACTION_EMAILS = gql`
  mutation resendMultiplePlatformActionEmail_UI($ids: [String!]!) {
    resendMultiplePlatformActionEmails(ids: $ids) {
      id
    }
  }
`

export const GET_PUBLIC_SUBCONTRACTOR_ACTIONS = gql`
  query publicSubcontractorPlatformActions_UI($subcontractorId: String!) {
    publicSubcontractorPlatformActions(subcontractorId: $subcontractorId) {
      id
      type
      status
      title
      description
      dueDate
      priorityType

      project {
        id
        status
        name
        timezone
        locale
        builder {
          id
          company {
            name
          }
          logoUrl
          defaultLocale
          defaultTimezone
        }
      }

      attachments {
        id
        key
        url
      }

      location {
        ...projectLocationResourceLinkWithParentAttributes
      }
    }
    publicSubcontractor(id: $subcontractorId) {
      id
      company {
        name
        abn
      }
      builder {
        id
        logoUrl
        company {
          name
        }
        defaultLocale
        defaultTimezone
      }
    }
  }
  ${PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES}
`

export const GET_PUBLIC_SUBCONTRACTOR_ACTION = gql`
  query publicSubcontractorPlatformAction_UI(
    $subcontractorId: String!
    $platformActionId: String!
  ) {
    publicSubcontractorPlatformAction(
      subcontractorId: $subcontractorId
      platformActionId: $platformActionId
    ) {
      id
      type
      status
      title
      description
      dueDate
      priorityType

      canCloseActions

      config {
        requireAttachmentToClose
      }

      project {
        id
        status
        name
        timezone
        locale

        builder {
          id
          company {
            name
          }
          logoUrl
          defaultLocale
          defaultTimezone
        }
      }

      location {
        ...projectLocationResourceLinkWithParentAttributes
      }

      attachments {
        id
        url
        thumbnail {
          small
        }
      }

      conversation {
        ...conversationThreadEntityAttributes
      }
    }
    publicSubcontractor(id: $subcontractorId) {
      id
      company {
        name
        abn
      }
      builder {
        id
        logoUrl
        company {
          name
        }
        defaultLocale
        defaultTimezone
      }
    }
  }
  ${CONVERSATION_THREAD_ENTITY_ATTRIBUTES}
  ${PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES}
`

export const UPDATE_PUBLIC_SUBCONTRACTOR_ACTION = gql`
  mutation updatePublicSubcontractorPlatformAction_UI(
    $subcontractorId: String!
    $platformActionId: String!
    $input: UpdatePublicSubcontractorPlatformActionInput!
  ) {
    updatePublicSubcontractorPlatformAction(
      subcontractorId: $subcontractorId
      platformActionId: $platformActionId
      input: $input
    ) {
      id
    }
  }
`

export const UPDATE_PLATFORM_ACTIONS_STATUS = gql`
  mutation updatePlatformActionsStatus_UI(
    $ids: [String!]!
    $status: PlatformActionStatus!
  ) {
    updatePlatformActionsStatus(ids: $ids, status: $status) {
      id
    }
  }
`

export const GET_ACTIONS_WORKER = gql`
  query platformActionsWorker_UI($projectId: String!) {
    platformActions_worker(projectId: $projectId) {
      id
      title
      description
      status
      priorityType
      type
      dueDate

      attachments {
        id
        key
        url
      }
    }
  }
`

export const GET_ACTION_WORKER = gql`
  query platformActionWorker_UI($actionId: String!) {
    platformAction_worker(id: $actionId) {
      id
      title
      description
      status
      priorityType
      type
      dueDate

      project {
        id
        name
        status
        timezone
        country
      }

      attachments {
        id
        key
        url
      }

      canCloseActions

      config {
        requireAttachmentToClose
      }

      location {
        ...projectLocationResourceLinkWithParentAttributes
      }

      conversation {
        ...conversationThreadEntityAttributes
      }
    }
  }
  ${CONVERSATION_THREAD_ENTITY_ATTRIBUTES}
  ${PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES}
`

export const CREATE_PLATFORM_ACTION_CONVERSATION_MESSAGE_WORKER = gql`
  mutation createPlatformActionConversationMessageWorker_UI(
    $actionId: String!
    $input: CreateConversationMessageInput!
  ) {
    createPlatformActionConversationMessage_worker(
      id: $actionId
      input: $input
    ) {
      id
    }
  }
`

export const UPDATE_PLATFORM_ACTION_WORKER = gql`
  mutation updatePlatformActionWorker_UI(
    $actionId: String!
    $input: UpdatePlatformActionInput_Worker!
  ) {
    updatePlatformAction_worker(id: $actionId, input: $input) {
      id
    }
  }
`
